import React, { useMemo } from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Icon from '@/components/Icon';
import { CATALOG_VIEW_MODES } from '@/components/Catalog/CatalogBrowseShell';

const isWeb = Platform.OS === 'web';

/**
 * Compact mobile catalog toolbar: optional view toggle + equal-width filter chips.
 * Pass `chipSlots` as an array of dropdown nodes (category, scope, sort, etc.).
 */
function CatalogMobileToolbar({
  viewMode,
  onViewModeChange,
  listMode = CATALOG_VIEW_MODES.LIST,
  gridMode = CATALOG_VIEW_MODES.GRID,
  showViewToggle = true,
  chipSlots = [],
  leading = null,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const listA11y = useMemo(() => {
    const v = t('pages.common.list');
    return v === 'pages.common.list' ? 'List' : v;
  }, [t]);

  const gridA11y = useMemo(() => {
    const v = t('pages.common.grid');
    return v === 'pages.common.grid' ? 'Grid' : v;
  }, [t]);

  return (
    <View
      style={[
        styles.shell,
        {
          backgroundColor: colors.solid_color_block,
          borderColor: colors.border_color_secondary,
        },
      ]}
    >
      {leading ? <View style={styles.leadingWrap}>{leading}</View> : null}
      <View style={styles.actionsRow}>
        {showViewToggle ? (
          <View
            style={[
              styles.viewToggle,
              { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_secondary },
            ]}
          >
            <Pressable
              onPress={() => onViewModeChange?.(listMode)}
              accessibilityLabel={listA11y}
              accessibilityRole="button"
              accessibilityState={{ selected: viewMode === listMode }}
              style={[
                styles.viewToggleBtn,
                { backgroundColor: viewMode === listMode ? colors.solid_color_tertiary : 'transparent' },
                isWeb && { cursor: 'pointer' },
              ]}
            >
              <Icon
                name="bars"
                size={12}
                color={viewMode === listMode ? colors.text_color_primary : colors.text_color_secondary}
              />
            </Pressable>
            <Pressable
              onPress={() => onViewModeChange?.(gridMode)}
              accessibilityLabel={gridA11y}
              accessibilityRole="button"
              accessibilityState={{ selected: viewMode === gridMode }}
              style={[
                styles.viewToggleBtn,
                { backgroundColor: viewMode === gridMode ? colors.solid_color_tertiary : 'transparent' },
                isWeb && { cursor: 'pointer' },
              ]}
            >
              <Icon
                name="grid-2"
                size={12}
                color={viewMode === gridMode ? colors.text_color_primary : colors.text_color_secondary}
              />
            </Pressable>
          </View>
        ) : null}

        {chipSlots.map((slot, index) => (
          <View key={slot?.key ?? `chip-${index}`} style={styles.chipSlot}>
            {slot}
          </View>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    width: '100%',
    borderRadius: 12,
    borderWidth: 1,
    paddingVertical: 10,
    paddingHorizontal: 10,
    gap: 10,
  },
  leadingWrap: {
    width: '100%',
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  viewToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    padding: 3,
    borderRadius: 10,
    borderWidth: 1,
    flexShrink: 0,
  },
  viewToggleBtn: {
    width: 34,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 7,
  },
  chipSlot: {
    flex: 1,
    minWidth: 0,
  },
});

export default React.memo(CatalogMobileToolbar);
